import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, BookOpen, Save } from 'lucide-react';

/**
 * In-app recipe editor for manager / head bartender.
 *
 * Loads the existing recipe from /api/menu/recipe/{itemId} (auth-gated to
 * bartender/manager/director on the backend), lets the staffer add / edit /
 * remove rows, then PUTs the whole list back. Each row is a free-text
 * (ingredient, amount) pair — including pseudo-rows like
 * ("Method", "Muddle then top with soda") and ("Glassware", "Highball")
 * which is the convention RecipePopover already renders.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   item: { id, name } | null
 */
export default function RecipeEditor({ open, onClose, item }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Monotonic row id source — keeps React keys stable when rows are
  // added / removed mid-list (index-as-key would have bound input state
  // to the wrong row after a delete).
  const nextRowKey = useRef(0);
  const blankRow = () => ({ _key: ++nextRowKey.current, ingredient: '', amount: '' });

  useEffect(() => {
    let cancelled = false;
    if (!open || !item?.id) {
      setRows([]);
      return () => { cancelled = true; };
    }
    setLoading(true);
    api.get(`/menu/recipe/${item.id}`)
      .then(res => {
        if (cancelled) return;
        const existing = (res.data?.recipe || []).map(r => ({
          _key: ++nextRowKey.current,
          ingredient: r.ingredient || '',
          amount: r.amount || '',
        }));
        // Always leave one blank row at the bottom so adding is one tap away.
        setRows(existing.length ? [...existing, blankRow()] : [blankRow()]);
      })
      .catch(err => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Could not load recipe');
        setRows([blankRow()]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, item?.id]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
      // Auto-append a fresh blank row when the last row gets filled, so the
      // staffer never has to hunt for a "+ Add" button.
      const last = next[next.length - 1];
      if (last && (last.ingredient || last.amount)) {
        next.push(blankRow());
      }
      return next;
    });
  };

  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!item?.id || saving) return;
    setSaving(true);
    try {
      // Backend drops fully-empty rows anyway, but trim here so toast count
      // matches what the bartender sees.
      const clean = rows
        .map(r => ({ ingredient: r.ingredient.trim(), amount: r.amount.trim() }))
        .filter(r => r.ingredient || r.amount);
      const { data } = await api.put(`/menu/recipe/${item.id}`, { recipe: clean });
      toast.success(`Recipe saved — ${data.rows} row${data.rows === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save recipe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-bg-secondary border border-white/10 rounded-sm max-w-lg" data-testid="recipe-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-gold" /> Recipe — {item?.name || '…'}
          </DialogTitle>
          <p className="text-[11px] text-zinc-500 font-sans">
            Free-form rows. For prep notes use rows like Method / Glassware / Garnish — the bartender popover renders all rows as-is.
          </p>
        </DialogHeader>
        {loading ? (
          <p className="text-zinc-500 text-sm font-sans py-8 text-center">Loading…</p>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="recipe-editor-rows">
            {rows.map((row, idx) => (
              <div key={row._key ?? idx} className="grid grid-cols-[1fr,140px,auto] gap-2 items-center" data-testid={`recipe-row-${idx}`}>
                <Input
                  value={row.ingredient}
                  onChange={(e) => updateRow(idx, 'ingredient', e.target.value)}
                  placeholder="Ingredient or label (e.g. White rum, Method, Glassware)"
                  className="bg-white/5 border-white/10 text-white font-sans text-sm rounded-sm"
                  data-testid={`recipe-ingredient-${idx}`}
                />
                <Input
                  value={row.amount}
                  onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                  placeholder="Amount / value"
                  className="bg-white/5 border-white/10 text-white font-sans text-sm rounded-sm"
                  data-testid={`recipe-amount-${idx}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(idx)}
                  className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                  disabled={rows.length === 1 && !row.ingredient && !row.amount}
                  data-testid={`recipe-remove-${idx}`}
                  title="Remove row"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRows(prev => [...prev, blankRow()])}
              className="w-full mt-2 text-zinc-400 hover:text-white font-sans text-xs gap-1.5 border border-dashed border-white/10"
              data-testid="recipe-add-row"
            >
              <Plus className="w-3 h-3" /> Add row
            </Button>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white font-sans text-sm" data-testid="recipe-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-gold text-black hover:bg-gold-light font-sans text-sm font-semibold rounded-sm"
            data-testid="recipe-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save recipe'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
